import api from './axios';
import { ApiResponse } from '../types';

export type Category = {
  id: string;
  name: string;
  slug: string;
  description?: string;
  is_active: boolean;
  display_order: number;
  product_count?: number;
  parent_category_id?: string | null;
  service_slug?: string | null;
  is_main_service?: boolean;
  created_at?: string;
  updated_at?: string;
};

export type Product = {
  id: string;
  name: string;
  description?: string;
  price: number;
  category: Category;
  category_id?: string;
  image_url: string;
  available_sizes: string[];
  stock_quantity?: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
};

export type ProductFilters = {
  page?: number;
  limit?: number;
  category?: string;
  search?: string;
  is_active?: boolean;
  sort?: string;
  order?: 'asc' | 'desc';
};

export type CreateProductPayload = {
  name: string;
  description?: string;
  category_id: string;
  price: number;
  available_sizes?: string[];
  stock_quantity?: number;
  is_active: boolean;
  image: File;
};

export type UpdateProductPayload = {
  name?: string;
  description?: string;
  category_id?: string;
  price?: number;
  available_sizes?: string[];
  stock_quantity?: number;
  is_active?: boolean;
  image?: File;
};

export type CreateCategoryPayload = {
  name: string;
  slug?: string;
  description?: string;
  display_order?: number;
  is_active?: boolean;
  service_slug?: string | null;
  parent_category_id?: string | null;
};

export type UpdateCategoryPayload = {
  name?: string;
  slug?: string;
  description?: string;
  display_order?: number;
  is_active?: boolean;
  service_slug?: string | null;
  parent_category_id?: string | null;
};

export type ProductsResponse = {
  data: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
};

// Public endpoints
export const getProducts = async (filters?: ProductFilters) => {
  const response = await api.get<ApiResponse<ProductsResponse>>('/api/v1/products', { params: filters });
  return response.data;
};

export const getProduct = async (id: string) => {
  const response = await api.get<ApiResponse<Product>>(`/api/v1/products/${id}`);
  return response.data;
};

// Admin endpoints
export const getAdminProducts = async (filters?: ProductFilters) => {
  const response = await api.get<ApiResponse<ProductsResponse>>('/api/v1/admin/products', { params: filters });
  return response.data;
};

export const getAdminProduct = async (id: string) => {
  const response = await api.get<ApiResponse<Product>>(`/api/v1/admin/products/${id}`);
  return response.data;
};

export const createProduct = async (formData: FormData) => {
  const response = await api.post<ApiResponse<Product>>('/api/v1/admin/products', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateProduct = async (id: string, formData: FormData) => {
  const response = await api.put<ApiResponse<Product>>(`/api/v1/admin/products/${id}`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteProduct = async (id: string) => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/admin/products/${id}`);
  return response.data;
};

// Public category endpoints
export const getCategoriesByServiceSlug = async (serviceSlug: string) => {
  const response = await api.get<ApiResponse<Category[]>>(`/api/v1/categories/service/${serviceSlug}`);
  return response.data;
};

export const getPublicCategories = async (filters?: {
  service_slug?: string;
  parent_category_id?: string;
  include_inactive?: boolean;
}) => {
  const response = await api.get<ApiResponse<Category[]>>('/api/v1/categories', { params: filters });
  return response.data;
};

// Admin category endpoints
export const getCategories = async () => {
  const response = await api.get<ApiResponse<Category[]>>('/api/v1/admin/categories');
  return response.data;
};

export const createCategory = async (payload: CreateCategoryPayload) => {
  const response = await api.post<ApiResponse<Category>>('/api/v1/admin/categories', payload);
  return response.data;
};

export const updateCategory = async (id: string, payload: UpdateCategoryPayload) => {
  const response = await api.put<ApiResponse<Category>>(`/api/v1/admin/categories/${id}`, payload);
  return response.data;
};

export const deleteCategory = async (id: string) => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/api/v1/admin/categories/${id}`);
  return response.data;
};
